import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import IMSLogo from '../../assets/IMS.svg';
import STFLogo from '../../assets/STF.svg';

// Global image cache to prevent reloading when returning to the page
const imageCache = {
  background: null,
  imsLogo: null,
  stfLogo: null,
  isLoaded: false
};

// Preload images immediately when module loads
const preloadImagesOnModuleLoad = () => {
  if (imageCache.isLoaded) return;
  
  // Preload logos
  const imsLogo = new Image();
  imsLogo.onload = () => {
    imageCache.imsLogo = imsLogo;
  };
  imsLogo.src = IMSLogo;

  const stfLogo = new Image();
  stfLogo.onload = () => {
    imageCache.stfLogo = stfLogo;
  };
  stfLogo.src = STFLogo;
};

// Start preloading immediately
preloadImagesOnModuleLoad();

const DirectionScreen = () => {
  const [selectedProject, setSelectedProject] = useState(null);
  const navigate = useNavigate();

  // Simple image preloading
  useEffect(() => {
    // Mark as loaded since we're using simple approach now
    imageCache.isLoaded = true;
  }, []);

  const projects = [
    {
      id: 'stf',
    
      fullName: 'Sales Tracking & Forecasting',
      description: 'Advanced analytics and sales performance tracking platform',
      icon: '📊',
      color: 'from-emerald-500 to-emerald-700',
      hoverColor: 'from-emerald-600 to-emerald-800',
      borderColor: 'border-emerald-200',
      url: 'https://www.kribhco.kinderkidz.in/login',
      isExternal: true
    },
    {
      id: 'ims',
      fullName: 'Inventory Management System',
      description: 'Comprehensive inventory and warehouse management solution',
      icon: '🏭',
      color: 'from-[#2691cc] to-[#1f7bb3]',
      hoverColor: 'from-[#1f7bb3] to-[#1a6ba3]',
      borderColor: 'border-[#2691cc]',
      route: '/login',
      isExternal: false
    }
  ];

  const handleProjectSelect = (project) => {
    setSelectedProject(project);
    
    setTimeout(() => {
      if (project.isExternal) {
        window.location.href = project.url;
      } else {
        navigate(project.route);
      }
    }, 800);
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-sky-300">

    {/* Subtle floating elements - optimized for mobile */}
    <div className="absolute top-10 sm:top-20 left-4 sm:left-20 w-20 h-20 sm:w-40 sm:h-40 bg-[#2691cc]/20 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob"></div>
    <div className="absolute top-20 sm:top-40 right-4 sm:right-20 w-20 h-20 sm:w-40 sm:h-40 bg-green-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-2000"></div>
    <div className="absolute bottom-10 sm:bottom-20 left-1/2 transform -translate-x-1/2 w-32 h-32 sm:w-60 sm:h-60 bg-[#2691cc]/10 rounded-full mix-blend-multiply filter blur-2xl opacity-40 animate-blob animation-delay-4000"></div>
    <div className="absolute top-1/2 left-2 sm:left-10 w-16 h-16 sm:w-32 sm:h-32 bg-sky-200 rounded-full mix-blend-multiply filter blur-xl opacity-50 animate-blob animation-delay-1000"></div>

    <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">
      {/* Header - KABL Title */}
      <div className="text-center mb-4 sm:mb-6">
        <h1 className="text-4xl sm:text-6xl md:text-8xl font-black text-white drop-shadow-lg mb-2 animate-fade-in">
          KABL
        </h1>
        <p className="text-sm sm:text-lg md:text-xl text-white/90 font-medium animate-fade-in animation-delay-200 px-4">
          Kribhco Agri Business Limited
        </p>
      </div>

      {/* Amazon-style Curved Arrow */}
      <div className="flex justify-center mb-8 sm:mb-12 animate-fade-in animation-delay-400">
        <div className="w-24 sm:w-40 h-6 sm:h-8 relative">
          <svg className="w-full h-full" viewBox="0 0 160 30" fill="none">
            <path 
              d="M20 20 Q80 5 140 20 L135 15 L140 20 L135 25" 
              stroke="white" 
              strokeWidth="3" 
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
              className="sm:stroke-[4]"
            />
          </svg>
        </div>
      </div>

      {/* Projects Grid - Two Containers */}
      <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12">
        {projects.map((project, index) => (
          <div
            key={project.id}
            className={`group relative rounded-2xl sm:rounded-3xl shadow-2xl transition-all duration-500 cursor-pointer transform hover:-translate-y-2 overflow-hidden flex flex-col justify-center items-center animate-fade-in animation-delay-${600 + index * 200} min-h-[200px] sm:min-h-[250px] lg:min-h-[300px] p-4 sm:p-6 ${
              project.id === 'stf' 
                ? 'bg-gradient-to-br from-green-600 to-green-700 border-2 border-green-500' 
                : 'bg-gradient-to-br from-[#2691cc] to-[#1f7bb3] border-2 border-[#2691cc]'
            }`}
            onClick={() => handleProjectSelect(project)}
          >
            {/* Logo Section */}
            <div className="text-center">
              <div className="mb-4 sm:mb-6">
                {project.id === 'ims' ? (
                  <div className="relative">
                    <img 
                      src={IMSLogo} 
                      alt="IMS Logo" 
                      className="w-48 sm:w-60 lg:w-72 h-auto filter brightness-0 invert mx-auto transition-all duration-300 hover:scale-105"
                      loading="eager"
                      decoding="async"
                    />
                  </div>
                ) : project.id === 'stf' ? (
                  <div className="relative">
                    <img 
                      src={STFLogo} 
                      alt="STF Logo" 
                      className="w-48 sm:w-60 lg:w-72 h-auto filter brightness-0 invert mx-auto transition-all duration-300 hover:scale-105"
                      loading="eager"
                      decoding="async"
                    />
                  </div>
                ) : (
                  <div className="text-6xl sm:text-8xl mx-auto">{project.icon}</div>
                )}
              </div>

              {/* Project Name */}
              <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3">
                {project.name}
              </h3>


            </div>
          </div>
        ))}
      </div>

      {/* Footer */}

    </div>

    {/* Enhanced Loading Overlay */}
    {selectedProject && (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-10 text-center shadow-2xl max-w-md mx-4">
          <div className="relative mb-6">
            <div className="w-16 h-16 border-4 border-gray-200 border-t-[#2691cc] rounded-full animate-spin mx-auto"></div>
            <div className="absolute inset-0 w-16 h-16 border-4 border-transparent border-t-emerald-600 rounded-full animate-spin mx-auto" style={{animationDelay: '0.2s'}}></div>
          </div>
          <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-3">
            Redirecting to {selectedProject.name}
          </h3>
          <p className="text-sm sm:text-base text-gray-600 mb-4">
            Please wait while we prepare your workspace...
          </p>
          <div className="flex justify-center space-x-1">
            <div className="w-2 h-2 bg-[#2691cc] rounded-full animate-bounce"></div>
            <div className="w-2 h-2 bg-emerald-600 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
            <div className="w-2 h-2 bg-purple-600 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
          </div>
        </div>
      </div>
    )}

    {/* Custom CSS for animations */}
    <style jsx>{`
      @keyframes fade-in {
        from { opacity: 0; transform: translateY(20px); }
        to { opacity: 1; transform: translateY(0); }
      }
      
      @keyframes blob {
        0% { transform: translate(0px, 0px) scale(1); }
        33% { transform: translate(30px, -50px) scale(1.1); }
        66% { transform: translate(-20px, 20px) scale(0.9); }
        100% { transform: translate(0px, 0px) scale(1); }
      }
      
      .animate-fade-in {
        animation: fade-in 0.8s ease-out forwards;
      }
      
      .animate-blob {
        animation: blob 7s infinite;
      }
      
      .animation-delay-200 {
        animation-delay: 0.2s;
      }
      
      .animation-delay-400 {
        animation-delay: 0.4s;
      }
      
      .animation-delay-600 {
        animation-delay: 0.6s;
      }
      
      .animation-delay-800 {
        animation-delay: 0.8s;
      }
      
      .animation-delay-1000 {
        animation-delay: 1s;
      }
      
      .animation-delay-2000 {
        animation-delay: 2s;
      }
      
      .animation-delay-4000 {
        animation-delay: 4s;
      }
      
      /* Mobile optimizations */
      @media (max-width: 640px) {
        .animate-blob {
          animation-duration: 10s;
        }
        
        .animate-fade-in {
          animation-duration: 0.6s;
        }
      }
      
      /* Reduce motion for users who prefer it */
      @media (prefers-reduced-motion: reduce) {
        .animate-blob,
        .animate-fade-in,
        .animate-spin,
        .animate-bounce {
          animation: none;
        }
      }
    `}</style>
  </div>
  );
};

export default DirectionScreen;
